import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, Building, Shield, FileText, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/users/${id}`);
      setClient(response.data.user);
    } catch (error) {
      console.error('Failed to fetch client details:', error);
      setError(error.response?.data?.error?.message || 'Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'SENT': 'bg-blue-100 text-blue-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'SUBMITTED': 'bg-purple-100 text-purple-800',
      'NEEDS_REVIEW': 'bg-orange-100 text-orange-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'DECLINED': 'bg-red-100 text-red-800',
      'ACTIVE': 'bg-green-100 text-green-800',
      'INACTIVE': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getRiskColor = (risk) => {
    const colors = {
      'LOW': 'bg-green-100 text-green-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'HIGH': 'bg-red-100 text-red-800'
    };
    return colors[risk] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Client</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/all-clients')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Clients
          </button>
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/all-clients')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to All Clients
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Client Details</h1>
      </div>

      {/* Client Information Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium text-xl">
                {client.first_name?.[0]}{client.last_name?.[0]}
              </span>
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {client.first_name} {client.last_name}
              </h2>
              <p className="text-sm text-gray-500">{client.user_type}</p>
            </div>
          </div>
          <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(client.status)}`}>
            {client.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center text-sm">
            <Mail className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium text-gray-900">{client.email}</p>
            </div>
          </div>
          <div className="flex items-center text-sm">
            <Phone className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-gray-500">Phone</p>
              <p className="font-medium text-gray-900">{client.phone || 'Not provided'}</p>
            </div>
          </div>
          <div className="flex items-center text-sm">
            <Calendar className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-gray-500">Created</p>
              <p className="font-medium text-gray-900">{new Date(client.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center text-sm">
            <User className="w-5 h-5 text-gray-400 mr-3" />
            <div>
              <p className="text-gray-500">Last Login</p>
              <p className="font-medium text-gray-900">
                {client.last_login_at ? new Date(client.last_login_at).toLocaleDateString() : 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Associated Companies */}
      {client.firms && client.firms.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Associated Companies
          </h3>
          <div className="space-y-3">
            {client.firms.map((firm, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{firm.firm_name}</p>
                  <p className="text-sm text-gray-500">Role: {firm.role}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(firm.status)}`}>
                  {firm.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Requests */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Verification Requests
        </h3>
        {client.verificationRequests && client.verificationRequests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {client.verificationRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/requests/${request.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{request.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{request.level}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{request.firm_name || '-'}</td>
                    <td className="px-4 py-3">
                      {request.ClientFile?.risk_level ? (
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRiskColor(request.ClientFile.risk_level)}`}>
                          {request.ClientFile.risk_level}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(request.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No verification requests found</p>
        )}
      </div>

      {/* KYB Data */}
      {client.kybData && client.kybData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            KYB Verification Data
          </h3>
          <div className="space-y-4">
            {client.kybData.map((kyb) => (
              <div key={kyb.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Entity Name</p>
                    <p className="font-medium text-gray-900">{kyb.entity_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Registration Number</p>
                    <p className="font-medium text-gray-900">{kyb.entity_registration_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Jurisdiction</p>
                    <p className="font-medium text-gray-900">{kyb.entity_jurisdiction}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Risk Level</p>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRiskColor(kyb.risk_level)}`}>
                      {kyb.risk_level} ({kyb.risk_score})
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(kyb.completion_status)}`}>
                      {kyb.completion_status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Decision</p>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(kyb.decision)}`}>
                      {kyb.decision || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;

